<template>
  <span class="zm-checkbox" :class="{'zm-checkbox--large': checkboxSize === 'large'}">
    <label
      class="zm-checkbox-wrap"
      :class="[
        { 'is-disabled': isDisabled },
        { 'is-checked': isChecked },
        { 'is-indeterminate': indeterminate },
        { 'is-disable-transition': disableTransition}
      ]"
    >
      <input
        ref="input"
        class="zm-checkbox__original"
        type="checkbox"
        :disabled="isDisabled"
        :value="label"
        :name="name"
        v-model="model"
        :aria-checked="indeterminate ? 'mixed': ((typeof isChecked === 'undefined')? 'false' : isChecked.toString())"
        :aria-label="ariaLabel || label"
        :aria-describedby="ariaLabelBy"
        @change="handleChange"
        @focus="focused = true"
        @blur="focused = false"
        @keydown.enter.stop="handleEnter"
        :id="itemId"
        :tabindex="isList ? -1 : tabindex ? tabindex : 0"
        :aria-controls="controls"
      >
      <span class="zm-checkbox__inner"></span>
      <span class="zm-checkbox__label" v-if="$slots.default || label">
        <span class="zm-checkbox__label--inner">
          <slot></slot>
          <template v-if="!$slots.default">{{label}}</template>
        </span>
        <span class="zm-checkbox__suffix" v-if="$slots.suffix">
          <slot name="suffix"></slot>
        </span>
      </span>
      <span v-else-if="ariaLabel" class="zm-sr-only">{{ariaLabel}}</span>
    </label>
  </span>
</template>
<script>
  import Emitter from '../../mixins/emitter';

  let _uid = 0;

  export default {
    name: 'ZmCheckbox',

    mixins: [Emitter],

    inject: {
      ZmForm: {
        default: ''
      },
      ZmFormItem: {
        default: ''
      }
    },

    componentName: 'ZmCheckbox',

    data() {
      return {
        selfModel: false,
        focused: false,
        isLimitExceeded: false
      };
    },

    computed: {
      model: {
        get() {
          return this.isGroupOrList
            ? this.store : this.value !== undefined
              ? this.value : this.selfModel;
        },

        set(val) {
          if (this.isGroup) {
            this.dispatch('ZmCheckboxGroup', 'input', [val]);
          } else if (this.isList) {
            this.dispatch('ZmCheckboxList', 'input', [val]);
          } else {
            this.$emit('input', val);
            this.selfModel = val;
          }
        }
      },

      isChecked() {
        if ({}.toString.call(this.model) === '[object Boolean]') {
          return this.model;
        } else if (Array.isArray(this.model)) {
          return this.model.indexOf(this.label) > -1;
        } else if (this.model !== null && this.model !== undefined) {
          return this.model === true;
        }
      },

      isGroup() {
        let parent = this.$parent;
        while (parent) {
          if (parent.$options.componentName !== 'ZmCheckboxGroup') {
            parent = parent.$parent;
          } else {
            this._checkboxGroupOrList = parent;
            return true;
          }
        }
        return false;
      },

      isList() {
        let parent = this.$parent;
        while (parent) {
          if (parent.$options.componentName !== 'ZmCheckboxList') {
            parent = parent.$parent;
          } else {
            this._checkboxGroupOrList = parent;
            return true;
          }
        }
        return false;
      },

      isGroupOrList() {
        return this.isList || this.isGroup;
      },

      store() {
        return this._checkboxGroupOrList ? this._checkboxGroupOrList.value : this.value;
      },

      isDisabled() {
        return this.isGroupOrList
          ? this._checkboxGroupOrList.disabled || this.disabled || (this.ZmForm || {}).disabled || (!this.isChecked && this.isExceeded())
          : this.disabled || (this.ZmForm || {}).disabled;
      },

      _elFormItemSize() {
        return (this.ZmFormItem || {}).elFormItemSize;
      },

      checkboxSize() {
        const temCheckboxSize = this._elFormItemSize || (this.$ELEMENT || {}).size || this.size;
        return this.isGroup
          ? this._checkboxGroupOrList.checkboxGroupSize || temCheckboxSize
          : temCheckboxSize;
      },

      itemId() {
        return this.id || `checkbox_${_uid++}`;
      }
    },

    props: {
      value: {},
      label: {},
      x: Number,
      y: Number,
      indeterminate: Boolean,
      disabled: Boolean,
      checked: Boolean,
      ariaLabel: String,
      ariaLabelBy: String,
      name: String,
      tabindex: [Number, String],
      id: String,
      controls: String,
      disableTransition: Boolean
    },

    methods: {
      focus() {
        this.focused = true;
        this.$refs.input.focus();
      },
      blur() {
        this.focused = false;
        this.$refs.input.focus();
      },
      addToStore() {
        if (Array.isArray(this.model) && this.model.indexOf(this.label) === -1) {
          this.model.push(this.label);
        } else {
          this.model = true;
        }
      },
      handleEnter(ev) {
        ev.target.checked = !ev.target.checked;
        if (this.isGroup || this.isList) {
          if (!Array.isArray(this.model)) {
            console.warn('Checkbox group or checkbox list model must be an array');
          } else {
            const clone = this.model.slice(0);
            if (clone.indexOf(this.label) === -1) {
              clone.push(this.label);
            } else {
              const index = clone.indexOf(this.label);
              clone.splice(index, 1);
            }
            this.model = clone;
          }
        } else {
          this.model = ev.target.checked;
        }
        this.handleChange(ev);
      },
      handleChange(ev) {
        if (this.isLimitExceeded) return;
        let value;
        if (ev.target.checked) {
          value = true;
        } else {
          value = false;
        }
        this.$emit('change', value, ev);
        this.$nextTick(() => {
          if (this.isGroup) {
            this.dispatch('ZmCheckboxGroup', 'change', [this._checkboxGroupOrList.value]);
          } else if (this.isList) {
            this.dispatch('ZmCheckboxList', 'change', [this._checkboxGroupOrList.value, this]);
          }
        });
      },
      isExceeded() {
        const limit = this._checkboxGroupOrList.limit;
        if (limit > 0) {
          return this.model.length >= limit;
        }
        return false;
      }
    },

    created() {
      this.checked && this.addToStore();
    },
    /*
    mounted() { // 为indeterminate元素 添加aria-controls 属性
      if (this.indeterminate) {
        this.$el.setAttribute('aria-controls', this.controls);
      }
    },
    */

    watch: {
      value(value) {
        this.dispatch('ZmFormItem', 'el.form.change', value);
      }
    }
  };
</script>

<style lang="scss">
  @import "../../scss/common/var";
  @import "../../scss/mixins/mixins";
  @import "../../scss/mixins/utils";

  @include b(checkbox) {
    color: $--checkbox-color;
    font-weight: $--checkbox-font-weight;
    font-size: inherit;
    display: inline-block;
    padding-top: 4px;
    padding-bottom: 4px;
    margin-right: 30px;
    line-height: 24px;
    &:last-child {
      margin-right: 0;
    }
    .is-indeterminate {
      .zm-checkbox__inner {
        background-color: $--checkbox-checked-input-fill;
        border-color: $--checkbox-checked-input-border-color;
        border-radius: $--checkbox-input-border-radius;
        &::before {
          content: '';
          position: absolute;
          display: block;
          background-color: $--checkbox-checked-icon-color;
          height: 3px;
          transform: scale(0.5);
          left: 0;
          right: 0;
          top: 6px;
        }

        &::after {
          display: none;
        }
      }
    }
    @include m(large) {
      padding-top: 8px;
      padding-bottom: 8px;
    }
    @include e(inner) {
      position: absolute;
      width: $--checkbox-input-width;
      height: $--checkbox-input-height;
      left: 0;
      top: 2px;
      margin-right: 8px;
      cursor: pointer;
      &:hover {
        border-color: $--checkbox-input-border-color-hover;
      }
      &::before {
        content: "";
        width: $--checkbox-input-width;
        height: $--checkbox-input-height;
        position: absolute;
        left: 0;

        border: $--checkbox-input-border;
        border-radius: $--checkbox-input-border-radius;
        box-sizing: border-box;
        background-color: $--checkbox-input-fill;
        transition: border-color .25s cubic-bezier(.71, -.46, .29, 1.46), background-color .25s cubic-bezier(.71, -.46, .29, 1.46);
      }
      &::after {
        box-sizing: content-box;
        content: "";
        border: 2px solid $--checkbox-checked-icon-color;
        border-left: 0;
        border-top: 0;
        height: 7px;
        left: 6px;
        position: absolute;
        top: 3px;
        transform: rotate(45deg) scaleY(0);
        width: 3px;
        transition: transform .15s ease-in .05s;
        transform-origin: center;
      }
    }

    @include e(original) {
      opacity: 0;
      outline: none;
      position: absolute;
      margin: 0;
      width: 0;
      height: 0;
      z-index: -1;
      transform: scale(0);

      &:checked + .zm-checkbox__inner {
        &::before {
          border-color: $--checkbox-checked-input-border-color;
          background-color: $--checkbox-checked-input-fill;
        }

        &::after {
          transform: rotate(45deg) scale(1);
        }
      }

      &:disabled + .zm-checkbox__inner {
        &::before {
          border-color: $--checkbox-disabled-input-border-color;
          background-color: $--checkbox-disabled-input-fill;
          opacity: 0.25;
          cursor: not-allowed;
        }

        &::after {
          cursor: not-allowed;
          border-color: $--color-white;
        }
      }
      &:disabled:checked + .zm-checkbox__inner {
        &::before {
          border-color: $--checkbox-disabled-checked-input-border-color;
          background-color: $--checkbox-disabled-checked-input-fill;
          opacity: 0.35;
        }

        &::after {
          border-color: $--color-white;
        }
      }
      &:disabled ~ .zm-checkbox__label {
        cursor: not-allowed;
        color: $--color-text-disabled;
      }
      /*&:focus:not(:active):not(.is-disabled) + .zm-checkbox__inner:before {
        box-shadow: 0 0 0 2px $--color-white, 0 0 0 4px $--color-primary;
      }*/
    }

    @include e(label) {
      font-size: inherit;
      word-break: break-word;
    }
    .zm-checkbox-wrap {
      cursor: default;
      position: relative;
      padding-left: 24px;
      min-height: 20px;
      line-height: 20px;
      display: inline-block;
      margin-bottom: 0;
    }

    .is-disable-transition {
      .zm-checkbox__inner::before, .zm-checkbox__inner::after {
        transition: none;
      }
    }
  }
  .zm-checkbox__suffix{
    .zm-tooltip.zm-button--icon{
      line-height: 12px;
      position: relative;
      top: 2px;
    }
  }

  .is-keyboard-event .zm-checkbox__original:focus:not(:active):not(.is-disabled) + .zm-checkbox__inner:before {
    box-shadow: 0 0 0 2px #fff, 0 0 0 4px $--color-primary;
  }
  @media (forced-colors: active) {
    .is-keyboard-event .zm-checkbox__original:focus:not(:active):not(.is-disabled) + .zm-checkbox__inner + .zm-checkbox__label .zm-checkbox__label--inner {
      outline: dotted thin;
    }
  }
</style>
